import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RecordExportData } from 'common/models/Common';
import { ExportRecords_Mailchimp, GetLists_Mailchimp } from 'services/CustomerIntegrationsService';
import ExportRecordsValidationDialog from './ExportRecordsValidationDialog';
import { StringIdStringValueWrapper } from 'common/models/GenericTypes';
import CustomContentConfirmationDialog from './Generic/CustomContentConfirmationDialog';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import RWTextFieldComponent from 'components/RWTextFieldComponent';

interface Props {
  open: boolean;
  records: RecordExportData[];
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

export default function ExportRecordsToMailchimpDialog({
  open,
  records,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [fetchedLists, setFetchedLists] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(true);
  const [mailchimpLists, setMailchimpLists] = useState<StringIdStringValueWrapper[]>([]);
  const [selectedList, setSelectedList] = useState('0');
  const [newListName, setNewListName] = useState('');

  useEffect(() => {
    const getLists = async () => {
      const res = await GetLists_Mailchimp();
      if (res) {
        setMailchimpLists(res);
        setFetchedLists(true);
      }
    };
    open && !fetchedLists && getLists();
  }, [open, fetchedLists]);

  const invalidRecords = useMemo(() => {
    return records.filter((r) => r.id && !r.isOptOut && !r.email);
  }, [records]);

  const noContactRecords = useMemo(() => {
    return records.filter((r) => !r.id);
  }, [records]);

  const optOutRecords = useMemo(() => {
    return records.filter((r) => r.isOptOut);
  }, [records]);

  const validRecords = useMemo(() => {
    return records.filter((r) => r.id && !r.isOptOut && Boolean(r.email));
  }, [records]);

  const continueCallback = useCallback(async () => {
    setShowValidationDialog(false);
  }, []);

  const onCloseCallback = useCallback(() => {
    closeHandler();
    setSelectedList('0');
    setNewListName('');
    setShowValidationDialog(true);
  }, [closeHandler]);

  const onConfirmExportCallback = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    const recordData: Partial<RecordExportData>[] = validRecords.map((r) => ({
      id: r.id,
      firstName: r.firstName,
      lastName: r.lastName,
      email: r.email,
    }));
    const res = await ExportRecords_Mailchimp(selectedList, recordData, newListName, errorHandler);
    if (res) {
      successHandler && successHandler('Records exported to Mail Chimp');
      onCloseCallback();
    }
    loadingHandler && loadingHandler(false);
  }, [
    selectedList,
    validRecords,
    newListName,
    loadingHandler,
    errorHandler,
    successHandler,
    onCloseCallback,
  ]);

  return (
    <>
      <ExportRecordsValidationDialog
        open={open && showValidationDialog}
        closeHandler={onCloseCallback}
        continueHandler={continueCallback}
        entityTypeId={2}
        invalidEmailRecords={invalidRecords}
        noPrimaryContactRecords={noContactRecords}
        optOutRecords={optOutRecords}
        validRecords={validRecords}
        totalRecords={records}
      />
      <CustomContentConfirmationDialog
        fullWidth
        open={open && !showValidationDialog}
        cancelHandler={onCloseCallback}
        confirmHandler={onConfirmExportCallback}
        title="Export to MailChimp"
        isConfirmButtonDisabled={selectedList === '0' && !newListName}
      >
        <Stack spacing={2} mt="10px">
          <TextField
            select
            label="Mailchimp List"
            value={selectedList}
            onChange={({ target }) => setSelectedList(target.value)}
          >
            <MenuItem value="0">Create New List</MenuItem>
            {mailchimpLists.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.value}
              </MenuItem>
            ))}
          </TextField>
          {selectedList === '0' && (
            <RWTextFieldComponent
              label="New List Name"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
            />
          )}
        </Stack>
      </CustomContentConfirmationDialog>
    </>
  );
}
